import {
  Button,
  Buttons,
  CancelButton,
  Checkbox,
  EmploymentTypeFilter,
  Icon,
  MemberStatusFilter,
  ModalCard,
  SearchInput,
  Table,
  Tooltip,
} from '~/components';
import { Card } from '~/components/ModalCard';
import { useApi, useWorkspace } from '~/contexts';
import { useFeatures } from '~/hooks';
import { colors } from '~/styles';
import Fuse from 'fuse.js';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { sortAlphabetically } from '~/utils';

const S = {
  Table: styled(Table)`
    font-size: 0.875rem;
    margin-top: 1rem;
    height: 25rem;
    overflow-y: auto;

    ${Table.Cell} {
      align-items: flex-start;
      height: 100%;
      min-height: unset;
      padding: 1rem;
    }
  `,

  Checkbox: styled.div`
    > label > div {
      background: ${colors.white};
      width: 1.125rem;
      height: 1.125rem;
      font-size: 0.5rem;
      margin: 0;
    }
  `,

  Modal: styled(ModalCard)`
    ${Card} {
      width: 64rem;
    }
  `,

  Header: styled(Table.Header)`
    ${({ sticky }) =>
      sticky &&
      css`
        position: sticky;
        top: 0;
        z-index: 1;
      `}
  `,

  Filters: styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 10rem 10rem;
    gap: 0.5rem;
    align-items: stretch;

    input {
      height: 2rem;
      font-size: 0.75rem;
    }

    @media (max-width: 48rem) {
      grid-template-columns: 1fr;
    }
  `,

  MemberText: styled.span`
    color: ${({ $inactive }) => ($inactive ? colors.grey40 : 'inherit')};
  `,

  TableState: styled.div`
    position: sticky;
    left: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    color: ${({ $error }) => ($error ? colors.danger : colors.grey40)};
    font-size: 0.875rem;
  `,
};

function WorkspaceMembersModal({ projectId, projectMembers, onChange, onClose }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const features = useFeatures();
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [memberStatusId, setMemberStatusId] = useState('active');
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const projectMembersById = useMemo(() => _.keyBy(projectMembers, 'memberId'), [projectMembers]);

  useEffect(() => {
    let isCancelled = false;

    (async () => {
      setIsLoading(true);
      setErrorMessage(null);
      setWorkspaceMembers([]);

      try {
        const [activeMembersResponse, inactiveMembersResponse] = await Promise.all([
          api.www.workspaces(workspace.id).projects(projectId).workspaceMembers({ size: 1000, isActive: 'true' }),
          api.www.workspaces(workspace.id).projects(projectId).workspaceMembers({ size: 1000, isActive: 'false' }),
        ]);

        if (isCancelled) return;

        setWorkspaceMembers(_.uniqBy([...activeMembersResponse.data, ...inactiveMembersResponse.data], 'id'));
      } catch (error) {
        if (isCancelled) return;

        setWorkspaceMembers([]);
        setErrorMessage(error?.message || 'Unable to load members.');
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [api, workspace.id, projectId]);

  const members = useMemo(() => {
    const typedMembers =
      employmentTypes.length === 0
        ? workspaceMembers
        : workspaceMembers.filter((member) =>
            employmentTypes.some((employmentType) => employmentType.id === member.employmentTypeId),
          );
    const activeMembers = sortAlphabetically(
      typedMembers.filter((member) => member.isActive),
      'name',
    );
    const inactiveMembers = sortAlphabetically(
      typedMembers.filter((member) => !member.isActive),
      'name',
    );

    if (memberStatusId === 'active') {
      return activeMembers;
    }

    if (memberStatusId === 'inactive') {
      return inactiveMembers;
    }

    return [...activeMembers, ...inactiveMembers];
  }, [workspaceMembers, memberStatusId, employmentTypes]);

  const options = useMemo(() => {
    if (!searchQuery) return members;

    const fuse = new Fuse(members, {
      keys: ['name', 'jobTitle.name', 'practice.name', 'discipline.name'],
      threshold: 0.4,
    });

    return fuse.search(searchQuery).map((result) => result.item);
  }, [members, searchQuery]);

  const selected = useMemo(() => {
    if (!selectedMembers) return {};

    const membersById = _.keyBy(workspaceMembers, 'id');
    const selectableOptions = options.filter(({ id }) => !projectMembersById[id]?.isActive);
    const optionsById = _.keyBy(selectableOptions, 'id');

    const allSelectedMembers = selectedMembers.filter(
      ({ id }) => !!membersById[id] && !projectMembersById[id]?.isActive,
    );
    const visibleSelectedMembers = allSelectedMembers.filter(({ id }) => !!optionsById[id]);

    return {
      members: allSelectedMembers,
      visibleMembers: visibleSelectedMembers,
      selectableVisibleMembers: selectableOptions,

      get some() {
        return this.members.length > 0;
      },

      get someVisible() {
        return this.visibleMembers.length > 0;
      },

      get hasSelectableVisible() {
        return this.selectableVisibleMembers.length > 0;
      },

      get all() {
        return this.hasSelectableVisible && this.visibleMembers.length === this.selectableVisibleMembers.length;
      },
    };
  }, [selectedMembers, workspaceMembers, options, projectMembersById]);

  const handleBulkSelectionChange = () => {
    setSelectedMembers((previousSelectedMembers) => {
      const optionIds = new Set(options.filter(({ id }) => !projectMembersById[id]?.isActive).map(({ id }) => id));

      if (selected.all) {
        return previousSelectedMembers.filter(({ id }) => !optionIds.has(id));
      }

      const selectedMemberIds = new Set(previousSelectedMembers.map(({ id }) => id));

      return [
        ...previousSelectedMembers,
        ...options.filter(({ id }) => !projectMembersById[id]?.isActive && !selectedMemberIds.has(id)),
      ];
    });
  };

  const handleSearchChange = ({ target: { value } }) => {
    setSearchQuery(value);
  };

  const handleStatusFilterChange = ({ value }) => {
    setMemberStatusId(value);
  };

  const handleEmploymentTypeFilterChange = ({ value }) => {
    setEmploymentTypes(value);
  };

  const handleAdd = () => {
    onChange(selected.members);
    onClose();
  };

  return (
    <S.Modal title="Add Members" onClose={onClose}>
      <ModalCard.Body style={{ marginBottom: 0 }}>
        <S.Filters>
          <SearchInput
            value={searchQuery}
            placeholder="All"
            materialPlaceholder="Search"
            materialAlwaysVisible
            onChange={handleSearchChange}
          />

          <div data-testid="member_type_filter">
            <EmploymentTypeFilter value={employmentTypes} onChange={handleEmploymentTypeFilterChange} />
          </div>

          <MemberStatusFilter placeholder="Status" value={memberStatusId} onChange={handleStatusFilterChange} />
        </S.Filters>

        <S.Table>
          <S.Header sticky>
            <Table.Column align="center" width="3rem">
              <S.Checkbox>
                <Checkbox
                  aria-label="Select all members"
                  disabled={!selected.hasSelectableVisible}
                  checked={selected.all || false}
                  partial={selected.someVisible && !selected.all}
                  onChange={handleBulkSelectionChange}
                />
              </S.Checkbox>
            </Table.Column>
            <Table.Column width="17rem">Name</Table.Column>
            <Table.Column width="14rem">Job Title</Table.Column>
            <Table.Column isVisible={features.practices} width="14rem">
              Practice
            </Table.Column>
            <Table.Column width="14rem">Discipline</Table.Column>
          </S.Header>

          <Table.Body showNoResults={!isLoading && !errorMessage}>
            {isLoading ? (
              <Table.Row>
                <S.TableState>
                  <Icon icon="spinner" spin />
                  Loading members...
                </S.TableState>
              </Table.Row>
            ) : errorMessage ? (
              <Table.Row>
                <S.TableState $error role="alert">
                  {errorMessage}
                </S.TableState>
              </Table.Row>
            ) : (
              options.map((member) => {
                const checked = !!selectedMembers.find((selectedMember) => selectedMember.id === member.id);
                const isInactive = !member.isActive;
                const projectMember = projectMembersById[member.id];
                const isActiveOnTeam = !!projectMember?.isActive;
                const tooltipMessage = isActiveOnTeam ? 'Member is already on the team.' : null;

                const handleSelectionChange = () => {
                  setSelectedMembers((previousSelectedMembers) =>
                    previousSelectedMembers.some((selectedMember) => selectedMember.id === member.id)
                      ? previousSelectedMembers.filter((selectedMember) => selectedMember.id !== member.id)
                      : [...previousSelectedMembers, member],
                  );
                };

                return (
                  <Table.Row key={member.id}>
                    <Table.Cell>
                      <Tooltip message={tooltipMessage} placement="right" style={{ display: 'inline-flex' }}>
                        <S.Checkbox>
                          <Checkbox
                            aria-label={`Select ${member.name}`}
                            disabled={isActiveOnTeam}
                            checked={checked}
                            onChange={handleSelectionChange}
                            data-testid={`${member.id}_checkbox`}
                          />
                        </S.Checkbox>
                      </Tooltip>
                    </Table.Cell>
                    <Table.Cell>
                      <S.MemberText $inactive={isInactive}>{member.name}</S.MemberText>
                    </Table.Cell>
                    <Table.Cell>
                      <S.MemberText $inactive={isInactive}>{member.jobTitle?.name}</S.MemberText>
                    </Table.Cell>
                    <Table.Cell>
                      <S.MemberText $inactive={isInactive}>{member.practice?.name}</S.MemberText>
                    </Table.Cell>
                    <Table.Cell>
                      <S.MemberText $inactive={isInactive}>{member.discipline?.name}</S.MemberText>
                    </Table.Cell>
                  </Table.Row>
                );
              })
            )}
          </Table.Body>
        </S.Table>
      </ModalCard.Body>

      <ModalCard.Footer>
        <Buttons align="right">
          <CancelButton onClick={onClose}>Close</CancelButton>

          <Button disabled={!selected.some} onClick={handleAdd} data-testid="add_selected_members_button">
            Add Members
          </Button>
        </Buttons>
      </ModalCard.Footer>
    </S.Modal>
  );
}

export default WorkspaceMembersModal;
